import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Animates a number from its previous value to `target` over `duration` ms.
 * Returns the formatted display string with two decimal places. The animation
 * runs only when `enabled` is true; toggling `enabled` snapshots the current
 * value so the next run resumes from where it left off.
 *
 * Used by the prototype Portfolio card, Stake form, and Withdraw form to
 * smoothly count earnings up when the user enters an amount or the page loads.
 */
export function useCountUp(target: number, enabled: boolean, duration = 600): string {
    const [display, setDisplay] = useState("0.00");
    const rafRef = useRef<number>(0);
    const startRef = useRef<number | null>(null);
    const fromRef = useRef(0);
    const currentRef = useRef(0);
    const lastTargetRef = useRef(0);
    const prevEnabledRef = useRef(false);

    const animate = useCallback(
        (timestamp: number) => {
            if (startRef.current === null) startRef.current = timestamp;
            const elapsed = timestamp - startRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = fromRef.current + (lastTargetRef.current - fromRef.current) * eased;
            currentRef.current = current;
            setDisplay(
                current.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })
            );
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            }
        },
        [duration]
    );

    lastTargetRef.current = target;

    useEffect(() => {
        const wasEnabled = prevEnabledRef.current;
        prevEnabledRef.current = enabled;

        if (!enabled) {
            cancelAnimationFrame(rafRef.current);
            // Snapshot current value so the next count-up starts from here.
            fromRef.current = currentRef.current;
            return;
        }

        if (!wasEnabled && enabled) {
            startRef.current = null;
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(animate);
        }

        return (): void => {
            cancelAnimationFrame(rafRef.current);
        };
    }, [target, enabled, animate]);

    return display;
}
